import { useEffect, useMemo, useRef, useState } from 'react';
import { Chat, useChat } from '@ohoshi/chat';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { getGardenSlug, getGardenName } from '../../lib/garden';
import { buildThemeScale } from '../../lib/themeColor';
import { useGardenMeta } from '../../lib/useGardenMeta';
import { useFaqQuestions, useFaq, loadWorkerProfile, METHOD_BASE } from '../../lib/faq';
import FaqGuide from '../../components/feature/FaqGuide';
import ParkingMap from '../../components/feature/ParkingMap';

// 🟥 ワーカーさん向けの「あおいに聞く」。
//    中身は Supabase の chat-worker（ワーカー向けに知っていることを絞った関数）。
//    園・法人向けの chat-help は評価やランクまで知っているので、ここからは絶対に呼ばない。
const GARDEN_SLUG = getGardenSlug();
const CHAT_ENDPOINT = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/chat-worker`;

// 🟥 最初に置く質問ボタンは、園が承認した「よくある質問」から出す（lib/faq.ts・正本はメソッド）
// 🟥 あおいが答えられないときは「園に確認しますね」と言う約束（chat-worker）。その言葉が出たら、園に引き取るカードを出す
const ESCALATE_PHRASE = '園に確認しますね';

export default function ChatPage() {
  useGardenMeta('あおいに聞く');
  const navigate = useNavigate();
  const suggestions = useFaqQuestions(GARDEN_SLUG, 6);
  const faq = useFaq(GARDEN_SLUG);
  const params = new URLSearchParams(window.location.search);
  // 🟥 空欄のチャットより先に、カテゴリ→質問→答えの案内を見せる（承認済みの答えがある園だけ）。?q= で来た人はチャットへ直行
  const [mode, setMode] = useState<'guide' | 'chat'>(params.get('q') ? 'chat' : 'guide');
  const [gardenQ, setGardenQ] = useState<string | null>(null);
  const [garden, setGarden] = useState<{ name: string; theme_color: string | null } | null>(null);

  useEffect(() => {
    supabase
      .from('gardens')
      .select('name,theme_color')
      .eq('slug', GARDEN_SLUG)
      .maybeSingle()
      .then(({ data }) => {
        if (data) setGarden(data as { name: string; theme_color: string | null });
      });
  }, []);

  const displayName = garden?.name ?? getGardenName(GARDEN_SLUG);
  const theme = useMemo(() => buildThemeScale(garden?.theme_color ?? '#9CA3AF'), [garden?.theme_color]);
  const accent = theme['--theme-500'];

  const chat = useChat({
    endpoint: CHAT_ENDPOINT,
    body: { gardenSlug: GARDEN_SLUG },
    sendHistory: true,
    avatar: '/images/aoi.jpg',
    initial: [
      {
        side: 'them',
        avatar: '/images/aoi.jpg',
        text:
          'おつかれさまです。きらり保育園のAI保育士、あおいです。\n' +
          'スポットワークで分からないこと、困ったことがあったら、なんでも聞いてください。',
      },
    ],
  });

  // フォームの質問ボタンから来たとき（/chat?q=...）は、その質問をそのまま送る
  const sentQ = useRef(false);
  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get('q');
    if (q && !sentQ.current) { sentQ.current = true; chat.send(q.slice(0, 300)); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const msgs = chat.props.messages as { side: string; text: string }[];
  const lastThem = [...msgs].reverse().find((m) => m.side === 'them');
  const needsGarden = !!lastThem && lastThem.text.includes(ESCALATE_PHRASE) && msgs.length > 1;
  const lastQuestion = [...msgs].reverse().find((m) => m.side === 'me')?.text ?? '';

  return (
    <div className="h-[100dvh] flex flex-col bg-white" style={theme as React.CSSProperties}>
      <header className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 flex-shrink-0">
        <button
          onClick={() => navigate('/')}
          aria-label="もどる"
          className="w-10 h-10 flex items-center justify-center rounded-full text-gray-600 hover:bg-gray-100 cursor-pointer"
        >
          <i className="ri-arrow-left-line text-2xl"></i>
        </button>
        <img
          src="/images/aoi.jpg"
          alt="あおい"
          className="w-10 h-10 rounded-full object-cover object-top border-2 border-white shadow flex-shrink-0 bg-white"
        />
        <div className="min-w-0">
          <p className="text-base font-bold text-gray-800 leading-tight">あおい</p>
          <p className="text-xs text-gray-500 truncate">{displayName}</p>
        </div>
      </header>

      {/* まだ何も聞いていない人のために、そのまま押せる質問を置く */}
      {mode === 'guide' && faq.loaded && faq.groups.length > 0 ? (
        <>
          {gardenQ && <EscalateCard key={gardenQ} question={gardenQ} accent={accent} />}
          <FaqGuide
            garden={GARDEN_SLUG}
            groups={faq.groups}
            initialCat={params.get('cat')}
            accent={accent}
            onAskAoi={(text) => { setMode('chat'); if (text) chat.send(text); }}
            onAskGarden={(q) => setGardenQ(q)}
          />
        </>
      ) : (<>
      {chat.props.messages.length <= 1 && (
        <div className="px-4 pt-3 flex flex-wrap gap-2 flex-shrink-0">
          <p className="w-full text-sm font-bold text-gray-700">こんなこと、あおいに聞けますよ！</p>
          {suggestions.map((s) => (
            <button
              key={s}
              onClick={() => chat.send(s)}
              className="text-sm text-gray-700 bg-white border border-gray-200 rounded-full px-3.5 py-2 shadow-sm active:scale-[0.98] transition-transform cursor-pointer"
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {needsGarden && <EscalateCard key={lastQuestion} question={lastQuestion} accent={accent} />}
      {/* 自由入力で駐車場を聞かれたときも、地図を添える */}
      {!needsGarden && /駐車|パーキング|車で|停め/.test(lastQuestion) && (
        <details className="mx-4 mt-3 flex-shrink-0 rounded-2xl border border-gray-200 bg-white" open>
          <summary className="px-4 py-3 text-base font-bold text-gray-800 cursor-pointer">駐車場の地図</summary>
          <div className="px-3 pb-3 max-h-[45dvh] overflow-y-auto"><ParkingMap garden={GARDEN_SLUG} accent={accent} /></div>
        </details>
      )}

      <div className="flex-1 min-h-0">
        <Chat {...chat.props} placeholder="あおいに聞く" accent={accent} copyable fill />
      </div>
      </>)}
    </div>
  );
}

// あおいが答えられなかった質問を、園に引き取ってもらう。返事はメールで届く（タイミーのメッセージや info@ に書かなくてよい）
function EscalateCard({ question, accent }: { question: string; accent: string }) {
  const p = loadWorkerProfile();
  const [name, setName] = useState(p?.name ?? '');
  const [email, setEmail] = useState(p?.email ?? '');
  const [state, setState] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  const ok = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());
  const send = async () => {
    setState('sending');
    try {
      const r = await fetch(`${METHOD_BASE}/api/public/timee-ask`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ garden: GARDEN_SLUG, name: name.trim(), email: email.trim(), question }),
      });
      setState(r.ok ? 'done' : 'error');
    } catch { setState('error'); }
  };
  return (
    <div className="mx-4 mt-3 rounded-2xl border-2 p-4 flex-shrink-0" style={{ borderColor: accent }}>
      {state === 'done' ? (
        <p className="text-base text-gray-800 leading-relaxed">園に聞いておきます。お返事は <b>{email}</b> にメールで届きます。</p>
      ) : (
        <>
          <p className="text-base font-bold text-gray-800">この質問を、園に聞いてもらいますか？</p>
          <p className="text-sm text-gray-600 mt-1">お返事はメールで届きます。</p>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="お名前"
            className="mt-3 w-full border border-gray-200 rounded-xl px-3 py-3 text-base" />
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="メールアドレス" type="email" inputMode="email"
            className="mt-2 w-full border border-gray-200 rounded-xl px-3 py-3 text-base" />
          {state === 'error' && <p className="text-sm text-red-600 mt-2">送れませんでした。時間をおいてもう一度お願いします。</p>}
          <button onClick={send} disabled={!ok || state === 'sending'}
            className="mt-3 w-full rounded-xl py-3 text-base font-bold text-white disabled:opacity-40 active:scale-[0.98] transition-transform"
            style={{ background: accent }}>
            {state === 'sending' ? '送っています…' : '園に聞いてもらう'}
          </button>
        </>
      )}
    </div>
  );
}
